import React from 'react';
import { NodeTreeItem } from 'djedi-json';
import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';

import { getExhibitionNode, getProductDetail } from 'api';
import { ExhibitionVariant, getExhibitionNodeAdmin as getStoryNodeAdmin } from 'api/endpoints/cms';
import { ObjectProps } from 'components/cms/Object';
import MetaTitle from 'components/MetaTitle';
import { DJEDI_OBJECT_TYPE } from 'consts/cms';
import ExhibitionRenderer from 'containers/ExhibitionRenderer';
import Layout from 'containers/Layout';
import Main from 'containers/Main';
import { ExhibitionProvider } from 'contexts/ExhibitionContext';
import { Product } from 'types/Product';
import { makeStringParam } from 'utils';

export type StoryDetailProps = { story?: NodeTreeItem; products?: Product[] };

const getProductIds = (story: NodeTreeItem) => {
  const storyChildren = Array.isArray(story.content.children) ? story.content.children : [];

  const productRefs = storyChildren
    .filter(({ type }) => type === DJEDI_OBJECT_TYPE)
    .map(v => {
      const content = v.content as ObjectProps;

      return content.value;
    });

  return Array.from(new Set(productRefs));
};

const ExhibitDetail: NextPage<StoryDetailProps> = ({
  story: passedStory,
  products: passedProducts,
}) => {
  const router = useRouter();
  const { preview, slug } = router.query;

  const isPreview = preview === 'true';

  const [products, setProducts] = React.useState(passedProducts);
  const [story, setStory] = React.useState(passedStory);

  const productsMap = products?.reduce<Record<number, Product>>((obj, product) => {
    obj[product.ref] = product;

    return obj;
  }, {});

  React.useEffect(() => {
    (async () => {
      if (isPreview) {
        try {
          const path = makeStringParam(slug);
          const storyResponse = await getStoryNodeAdmin({
            path,
            variant: ExhibitionVariant.STORY,
          });

          if (storyResponse?.data?.results.length) {
            const { data } = storyResponse;
            const story = data.results[0].values;
            const productIds = getProductIds(story);

            const productsResponse = await Promise.all(
              productIds.map(id => getProductDetail({ id })),
            );
            const products = productsResponse.map(({ data }) => data);

            setProducts(products as Product[]);
            setStory(story);
          }
        } catch (error) {
          console.error(error);
        }
      }
    })();
  }, [isPreview, slug]);

  return (
    <>
      <MetaTitle>Story</MetaTitle>
      <Layout>
        <Main>
          {story && productsMap && (
            <ExhibitionProvider productsMap={productsMap}>
              <ExhibitionRenderer tree={story} />
            </ExhibitionProvider>
          )}
        </Main>
      </Layout>
    </>
  );
};

export default ExhibitDetail;

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { slug } = ctx.params || {};
  const { preview } = ctx.query;

  const isPreview = preview === 'true';
  const path = makeStringParam(slug);

  const storyResponse = !isPreview
    ? await getExhibitionNode({ path, variant: ExhibitionVariant.STORY })
    : undefined;

  if (storyResponse?.data?.length) {
    const { data } = storyResponse;
    const story = data[0].values;
    const productIds = getProductIds(story);

    const productsResponse = await Promise.all(productIds.map(id => getProductDetail({ id })));
    const products = productsResponse.map(({ data }) => data);

    return {
      props: { story, products },
    };
  }

  if (isPreview) {
    return {
      props: {
        story: null,
        products: null,
      },
    };
  }

  return { notFound: true };
};
